import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Chip,
  Button,
  Divider,
  useTheme,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  SmartToy as BotIcon,
  ContentCopy as CopyIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import FormFieldRenderer from './FormFieldRenderer';
import { escapeHtml } from '../utils/safeHtml';

const messageType = {
  answer: 'answer',
  question: 'question',
  form: 'form',
  system: 'system',
};

function ChatMessageRenderer({
  message,
  onFormSubmit,
  onFormFieldChange,
  formData = {},
  isActive = false,
  onActivate,
  darkMode = false,
  isNewMessage = false,
  isProcessing = false,
}) {
  const theme = useTheme();
  const [isTyping, setIsTyping] = useState(false);
  const [typedContent, setTypedContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Typewriter effect for text messages - only for new messages
  React.useEffect(() => {
    if (message.type === messageType.answer && message.content && isNewMessage && !isTyping) {
      setIsTyping(true);
      let index = 0;
      const typeInterval = setInterval(() => {
        if (index < message.content.length) {
          setTypedContent(message.content.substring(0, index + 1));
          index++;
        } else {
          setIsTyping(false);
          clearInterval(typeInterval);
        }
      }, 20);
      return () => clearInterval(typeInterval);
    } if (message.type === messageType.answer && message.content && !isNewMessage) {
      // For old messages, show full content immediately
      setTypedContent(message.content);
      setIsTyping(false);
    }
  }, [message.content, message.type, isNewMessage]);

  // Reset typing state when message changes
  React.useEffect(() => {
    if (!isNewMessage) {
      setTypedContent(message.content || '');
      setIsTyping(false);
    }
  }, [message.content, isNewMessage]);

  // Copy message content
  const handleCopy = () => {
    if (message.content) {
      navigator.clipboard.writeText(message.content);
    }
  };

  // Render different message types
  const renderMessageContent = () => {
    switch (message.type) {
      case messageType.form:
        return renderFormMessage();

      case messageType.system:
        return renderSystemMessage();

      case messageType.question:
        return renderTextMessage(message.content, 'user');

      case messageType.answer:
        return renderTextMessage(isTyping ? typedContent : message.content, 'bot');

      default:
        return renderTextMessage(message.content, 'user');
    }
  };

  const renderTextMessage = (content, sender) => {
    // Handle different content types
    let displayContent = content;
    let isJsonResponse = false;

    // Check if content is a JSON object or string that should be parsed
    if (typeof content === 'object' && content !== null) {
      // For AI responses, extract the readable text content
      if (sender === 'bot' && content.ai_response) {
        // Extract AI response text
        const aiResponse = content.ai_response;
        if (typeof aiResponse === 'object') {
          // Extract key information from AI response - generic approach
          const parts = [];
          Object.entries(aiResponse).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
              const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
              if (Array.isArray(value)) {
                parts.push(`**${formattedKey}:** ${value.join(', ')}`);
              } else if (typeof value === 'object') {
                parts.push(`**${formattedKey}:** ${JSON.stringify(value)}`);
              } else {
                parts.push(`**${formattedKey}:** ${value}`);
              }
            }
          });
          displayContent = parts.join('\n\n');
        } else if (typeof aiResponse === 'string') {
          displayContent = aiResponse;
        } else {
          displayContent = JSON.stringify(aiResponse, null, 2);
          isJsonResponse = true;
        }
      } else if (sender === 'bot' && typeof content === 'object') {
        // Generic object response - extract readable content
        const parts = [];
        Object.entries(content).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '' && key !== 'type' && key !== 'timestamp') {
            const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
            if (Array.isArray(value)) {
              parts.push(`**${formattedKey}:** ${value.join(', ')}`);
            } else if (typeof value === 'object') {
              parts.push(`**${formattedKey}:** ${JSON.stringify(value)}`);
            } else {
              parts.push(`**${formattedKey}:** ${value}`);
            }
          }
        });
        displayContent = parts.join('\n\n');
      } else if (sender === 'bot' && content.response) {
        // Generic AI response
        displayContent = content.response;
      } else if (sender === 'bot' && content.message) {
        // Message format
        displayContent = content.message;
      } else if (sender === 'bot' && content.text) {
        // Text format
        displayContent = content.text;
      } else if (sender === 'bot' && content.content) {
        // Content format
        displayContent = content.content;
      } else if (sender === 'bot' && typeof content === 'string') {
        // Plain text response
        displayContent = content;
      } else {
        // For other objects, format as JSON
        displayContent = JSON.stringify(content, null, 2);
        isJsonResponse = true;
      }
    } else if (typeof content === 'string') {
      // Try to parse as JSON if it looks like JSON
      try {
        const parsed = JSON.parse(content);
        if (typeof parsed === 'object' && parsed !== null) {
          // For AI responses in string format, extract readable content
          if (sender === 'bot' && parsed.ai_response) {
            const aiResponse = parsed.ai_response;
            if (typeof aiResponse === 'object') {
              const parts = [];
              Object.entries(aiResponse).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                  const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
                  if (Array.isArray(value)) {
                    parts.push(`**${formattedKey}:** ${value.join(', ')}`);
                  } else if (typeof value === 'object') {
                    parts.push(`**${formattedKey}:** ${JSON.stringify(value)}`);
                  } else {
                    parts.push(`**${formattedKey}:** ${value}`);
                  }
                }
              });
              displayContent = parts.join('\n\n');
            } else if (typeof aiResponse === 'string') {
              displayContent = aiResponse;
            } else {
              displayContent = JSON.stringify(aiResponse, null, 2);
              isJsonResponse = true;
            }
          } else if (sender === 'bot' && typeof parsed === 'object') {
            // Generic object response - extract readable content
            const parts = [];
            Object.entries(parsed).forEach(([key, value]) => {
              if (value !== null && value !== undefined && value !== '' && key !== 'type' && key !== 'timestamp') {
                const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
                if (Array.isArray(value)) {
                  parts.push(`**${formattedKey}:** ${value.join(', ')}`);
                } else if (typeof value === 'object') {
                  parts.push(`**${formattedKey}:** ${JSON.stringify(value)}`);
                } else {
                  parts.push(`**${formattedKey}:** ${value}`);
                }
              }
            });
            displayContent = parts.join('\n\n');
          } else if (sender === 'bot' && parsed.response) {
            // Generic AI response
            displayContent = parsed.response;
          } else if (sender === 'bot' && parsed.message) {
            // Message format
            displayContent = parsed.message;
          } else if (sender === 'bot' && parsed.text) {
            // Text format
            displayContent = parsed.text;
          } else if (sender === 'bot' && parsed.content) {
            // Content format
            displayContent = parsed.content;
          } else {
            displayContent = JSON.stringify(parsed, null, 2);
            isJsonResponse = true;
          }
        }
      } catch (e) {
        // Not JSON, use as-is
        displayContent = content;
      }
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {isJsonResponse ? (
          // Render JSON responses in a formatted way
          <Box
            sx={{
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
              p: 2,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '400px',
              overflow: 'auto',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                lineHeight: 1.4,
              }}
            >
              {displayContent}
            </Typography>
          </Box>
        ) : (
          // Render regular text with markdown-style formatting
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.6,
              color: sender === 'bot' ? 'text.primary' : 'text.primary',
              '& strong': {
                fontWeight: 'bold',
                color: theme.palette.primary.main,
              },
            }}
            component="div"
            dangerouslySetInnerHTML={{
              __html: escapeHtml(displayContent)
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\n/g, '<br />'),
            }}
          />
        )}

        {sender === 'bot' && content && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Tooltip title="Copy message">
              <IconButton size="small" onClick={handleCopy}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    );
  };

  const renderFormMessage = () => {
    const { formFields = [], title, description } = message.content || {};

    const safeFormFields = Array.isArray(formFields) ? formFields : [];
    return (
      <Paper
        elevation={2}
        sx={{
          p: 3,
          border: 2,
          borderColor: isActive ? 'primary.main' : 'divider',
          bgcolor: isActive ? 'action.hover' : 'background.paper',
          cursor: isActive ? 'default' : 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
        }}
        onClick={() => onActivate && onActivate()}
      >
        {/* Form Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {title || 'Form'}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>

        {/* Form Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {safeFormFields.map((field, index) => (
            <FormFieldRenderer
              key={field.id || index}
              type={field.type}
              label={field.label}
              placeholder={field.placeholder}
              value={formData[field.name || field.id] || field.value || ''}
              onChange={(e) => onFormFieldChange && onFormFieldChange(field.name || field.id, e.target.value)}
              required={field.required}
              disabled={field.disabled || false} // Only disable if explicitly set
              options={field.options || []}
              min={field.min}
              max={field.max}
              step={field.step}
              {...field.props}
            />
          ))}
        </Box>

        {/* Form Actions */}
        {(isActive || isNewMessage) && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={async () => {
                if (onFormSubmit) {
                  setIsSubmitting(true);
                  try {
                    // CRITICAL FIX: Use message.content.formId (actual form node ID) instead of message.id (message ID)
                    const formId = message.content?.formId || message.id;
                    console.log('🔍 Form submission - using formId:', formId, 'formData:', formData);
                    await onFormSubmit(formId, formData);
                  } catch (error) {
                    console.error('Form submission error:', error);
                  } finally {
                    setIsSubmitting(false);
                  }
                }
              }}
              disabled={!safeFormFields.some((field) => (field.required ? formData[field.name || field.id] : true)) || isSubmitting || isProcessing}
              startIcon={(isSubmitting || isProcessing) ? <CircularProgress size={16} /> : null}
            >
              {isSubmitting ? 'Submitting...' : isProcessing ? 'AI is thinking...' : 'Submit'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => onActivate && onActivate()}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Paper>
    );
  };

  const renderSystemMessage = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        my: 1,
      }}
    >
      <Chip
        label={message.content}
        size="small"
        color="info"
        variant="outlined"
      />
    </Box>
  );

  // Determine message alignment and styling
  const isUserMessage = message.type === messageType.question;
  const isBotMessage = message.type === messageType.answer || message.type === messageType.form;
  const isSystemMessage = message.type === messageType.system;

  if (isSystemMessage) {
    return renderSystemMessage();
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUserMessage ? 'flex-end' : 'flex-start',
        mb: 2,
        maxWidth: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          maxWidth: isUserMessage ? '70%' : '85%',
          flexDirection: isUserMessage ? 'row-reverse' : 'row',
        }}
      >
        {/* Avatar */}
        <Avatar
          sx={{
            bgcolor: isUserMessage ? 'primary.main' : 'secondary.main',
            width: 32,
            height: 32,
            flexShrink: 0,
          }}
        >
          {isUserMessage ? <PersonIcon /> : <BotIcon />}
        </Avatar>

        {/* Message Content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minWidth: 0,
            flex: 1,
          }}
        >
          {/* Username */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              textAlign: isUserMessage ? 'right' : 'left',
              fontWeight: 500,
            }}
          >
            {isUserMessage ? 'You' : 'AI Assistant'}
          </Typography>

          {/* Message Bubble */}
          <Paper
            elevation={1}
            sx={{
              p: 2,
              bgcolor: isUserMessage ? 'primary.main' : 'background.paper',
              color: isUserMessage ? 'primary.contrastText' : 'text.primary',
              borderRadius: 2,
              border: isUserMessage ? 'none' : 1,
              borderColor: 'divider',
              maxWidth: '100%',
              wordBreak: 'break-word',
            }}
          >
            {renderMessageContent()}
          </Paper>

          {/* Timestamp */}
          {message.timestamp && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                textAlign: isUserMessage ? 'right' : 'left',
                fontSize: '0.75rem',
              }}
            >
              {new Date(message.timestamp).toLocaleTimeString()}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default ChatMessageRenderer;
